import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import PhotoUpload from "@/components/PhotoUpload";
import GalleryUpload from "@/components/GalleryUpload";
import LocationPicker from "@/components/LocationPicker";
import CategoryPicker from "@/components/CategoryPicker";
import { toast } from "sonner";
import { ArrowLeft, MapPin } from "lucide-react";
import { Link } from "react-router-dom";

export default function BusinessForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const isEdit = Boolean(id);
  const [form, setForm] = useState({
    name: "", category: "", description: "", address: "",
    whatsapp: "", website: "", instagram: "", photo_path: "",
    gallery: [], lat: "", lng: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (isEdit) (async () => {
    try {
      const { data } = await api.get(`/businesses/${id}`);
      setForm({
        name: data.name || "", category: data.category || "", description: data.description || "",
        address: data.address || "", whatsapp: data.whatsapp || "", website: data.website || "",
        instagram: data.instagram || "", photo_path: data.photo_path || "",
        gallery: data.gallery || [],
        lat: data.lat ?? "", lng: data.lng ?? "",
      });
    } catch (err) { console.error("Failed to load business:", err); toast.error("Gagal memuat bisnis"); }
  })(); }, [id, isEdit]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.photo_path) { toast.error("Foto bisnis wajib diunggah"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        lat: form.lat === "" ? null : parseFloat(form.lat),
        lng: form.lng === "" ? null : parseFloat(form.lng),
      };
      if (isEdit) await api.patch(`/businesses/${id}`, payload);
      else await api.post("/businesses", payload);
      toast.success(isEdit ? "Bisnis diperbarui" : "Bisnis ditambahkan");
      nav("/app/me");
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  return (
    <Layout>
      <Link to="/app/me" className="inline-flex items-center gap-2 text-sm text-bizma-subtext mb-6" data-testid="back-to-profile">
        <ArrowLeft size={16}/> Kembali
      </Link>
      <div className="max-w-2xl">
        <h1 className="text-3xl font-display font-bold">{isEdit ? "Edit Bisnis" : "Tambah Bisnis"}</h1>
        <p className="text-bizma-subtext mt-1">Setiap bisnis wajib memiliki foto usaha.</p>
        <form onSubmit={submit} className="felt-card p-6 sm:p-8 mt-6 space-y-5" data-testid="business-form">
          <div>
            <label className="text-sm font-medium">Foto Bisnis <span className="text-bizma-terracotta">*</span></label>
            <div className="mt-2">
              <PhotoUpload value={form.photo_path} onChange={(p) => setForm({ ...form, photo_path: p })} shape="square" testId="biz-photo" />
            </div>
          </div>
          <F label="Nama Bisnis" required><input required value={form.name} onChange={upd("name")} className="input-bizma" data-testid="biz-name" /></F>
          <F label="Kategori" required>
            <CategoryPicker value={form.category} onChange={(v) => setForm({ ...form, category: v })} testId="biz-category" />
          </F>
          <F label="Deskripsi" required><textarea required rows={4} value={form.description} onChange={upd("description")} className="input-bizma h-auto py-3" data-testid="biz-description" /></F>
          <F label="Alamat"><input value={form.address} onChange={upd("address")} className="input-bizma" data-testid="biz-address" /></F>
          <F label="WhatsApp Bisnis"><input value={form.whatsapp} onChange={upd("whatsapp")} className="input-bizma" placeholder="6281234567890" data-testid="biz-whatsapp" /></F>
          <F label="Website"><input value={form.website} onChange={upd("website")} className="input-bizma" placeholder="https://" data-testid="biz-website" /></F>
          <F label="Instagram"><input value={form.instagram} onChange={upd("instagram")} className="input-bizma" placeholder="@username" data-testid="biz-instagram" /></F>
          <div>
            <label className="text-sm font-medium">Galeri Foto (opsional)</label>
            <div className="mt-2">
              <GalleryUpload value={form.gallery} onChange={(g) => setForm({ ...form, gallery: g })} testId="biz-gallery" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium flex items-center gap-2"><MapPin size={14}/> Lokasi Bisnis</label>
            <div className="mt-2">
              <LocationPicker
                lat={form.lat}
                lng={form.lng}
                onChange={({ lat, lng }) => setForm((f) => ({ ...f, lat, lng }))}
              />
            </div>
          </div>
          <button disabled={busy} className="btn-primary w-full h-12" data-testid="biz-submit">{busy ? "Menyimpan..." : (isEdit ? "Simpan Perubahan" : "Tambahkan Bisnis")}</button>
        </form>
      </div>
    </Layout>
  );
}

function F({ label, required, children }) {
  return <div><label className="text-sm font-medium">{label}{required && <span className="text-bizma-terracotta"> *</span>}</label><div className="mt-2">{children}</div></div>;
}
